import { LockedHint } from "@/components/predictions/LockedHint";
import { uiText } from "@/lib/i18n/uiText";
import { createFileRoute, notFound } from "@tanstack/react-router";
import NotFound from "@/pages/NotFound";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import FixtureDetail from "@/pages/FixtureDetail";
import { MainLayout } from "@/components/layout/MainLayout";
import { readableFixture, socialMeta } from "@/lib/routeSocial";
import { getPublicFixture } from "@/lib/publicPicks.functions";
import { GATES_SUSPENDED } from "@/lib/accessMode";

const SITE = "https://statlign.com";

const fixtureQuery = (slug: string) =>
  queryOptions({
    queryKey: ["public-fixture", slug],
    queryFn: () => getPublicFixture({ data: { slug } }),
    staleTime: 5 * 60_000,
  });

export const Route = createFileRoute("/fixtures/$slug")({
  // Malformed fixture slugs are never real pages: answer with a true 404.
  beforeLoad: ({ params }) => {
    if (!/^.+-vs-.+$/.test(params.slug)) throw notFound();
  },
  notFoundComponent: NotFound,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(fixtureQuery(params.slug)),
  head: ({ params, loaderData }) => {
    const { name, date } = readableFixture(params.slug);
    const f = loaderData ?? null;
    const title = f ? `${f.team_home} vs ${f.team_away}` : name;
    const finished = f ? new Date(f.kickoff_time).getTime() < Date.now() : false;
    const scripts = f
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SportsEvent",
              name: title,
              sport: "Soccer",
              startDate: f.kickoff_time,
              eventStatus: "https://schema.org/EventScheduled",
              ...(f.home_score != null && f.away_score != null
                ? { description: `Final score: ${f.team_home} ${f.home_score}–${f.away_score} ${f.team_away}.` }
                : {}),
              eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
              url: `${SITE}/fixtures/${params.slug}`,
              location: { "@type": "Place", name: `${f.team_home} home ground` },
              homeTeam: { "@type": "SportsTeam", name: f.team_home },
              awayTeam: { "@type": "SportsTeam", name: f.team_away },
              competitor: [
                { "@type": "SportsTeam", name: f.team_home },
                { "@type": "SportsTeam", name: f.team_away },
              ],
              ...(f.league ? { superEvent: { "@type": "SportsEvent", name: f.league } } : {}),
              organizer: { "@type": "Organization", name: f.league ?? "Football", url: SITE },
              ...(f.home_score == null ? { description: f.reason ?? `${title} prediction and analysis by Statlign.` } : {}),
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Fixtures", item: `${SITE}/fixtures` },
                { "@type": "ListItem", position: 3, name: title, item: `${SITE}/fixtures/${params.slug}` },
              ],
            }),
          },
        ]
      : [];
    return {
      meta: [...(f ? [] : [{ name: "robots", content: "noindex, follow" }]), ...socialMeta({
        seo: {
          topics: ["fixtures", "footballPredictions"],
          entities: f ? [`${f.team_home} vs ${f.team_away} prediction`, f.team_home, f.team_away, ...(f.league ? [`${f.league} predictions`] : [])] : [],
        },
        title: finished
          ? `${title} Result & Prediction Review${date ? ` — ${date}` : ""} | Statlign`
          : `${title} Prediction, Tips & Odds${date ? ` — ${date}` : ""} | Statlign`,
        description: finished
          ? `${title}: how the Statlign model pick, odds and confidence compared with the final result.`
          : `${title} prediction with model pick, odds, confidence and the full reasoning from the Statlign 50-strategy engine.`,
        ogType: "article",
      })],
      scripts,
    };
  },
  component: FixtureRoute,
});

function FixtureRoute() {
  const { slug } = Route.useParams();
  const { data: f } = useSuspenseQuery(fixtureQuery(slug));
  const finished = f ? new Date(f.kickoff_time).getTime() < Date.now() : false;
  return (
    <MainLayout>
      {f && (
        <section className="container mx-auto px-4 mt-2 mb-6 rounded-xl border border-border bg-card/40 p-5" aria-label={uiText("Fixture summary")}>
          <h1 className="text-xl font-semibold mb-2">
            {f.team_home}  {uiText("vs")} {f.team_away}: {finished ? uiText("prediction review") : uiText("prediction summary")}
          </h1>
          <p className="text-sm text-muted-foreground mb-2">
            {f.league ? `${f.league} · ` : ""}
            {new Date(f.kickoff_time).toUTCString()}
          </p>
          <p className="text-sm">
            {uiText(f.market_type)}: {GATES_SUSPENDED || finished ? <strong>{uiText(f.predicted_outcome)}</strong> : <LockedHint variant="view" />}
            {f.odds ? uiText(" · odds {{v0}}", { v0: f.odds.toFixed(2) }) : ""}
            {f.confidence_score != null ? uiText(" · {{v0}}% confidence", { v0: Math.round(f.confidence_score) }) : ""}
          </p>
          {finished && (
            <p className="text-sm mt-2">
              {f.home_score != null && f.away_score != null ? (
                <>{uiText("Final score:")} <strong>{f.team_home} {f.home_score}–{f.away_score} {f.team_away}</strong> · </>
              ) : null}
               {uiText("Outcome:")} {f.actual_outcome ? <strong>{f.actual_outcome}</strong> : uiText("awaiting verified final score")}</p>
          )}
          {(GATES_SUSPENDED || finished) && f.reason ? <p className="text-xs text-muted-foreground mt-2">{f.reason}</p> : null}
        </section>
      )}
      <FixtureDetail />
    </MainLayout>
  );
}
